import {
  TOPIC_CONTENT_SOURCES,
  type TopicContentSource,
} from "./topic-content-sources";
import { TOPIC_GUIDES, type TopicGuide } from "./topic-guides";

export const DEBT_CREDIT_GUIDE = {
  topicId: "debt-credit",
  locale: "ko",
  publicationStatus: "korean_test_preview",
  risk: "R3",
  checkedAt: "2026-07-16",
  jurisdictionLabel: "호주 전역",
  summary:
    "빚을 감당하기 어렵다면 새 대출로 급히 막기 전에 모든 채무·연체·법원 기한과 필수 생활비를 한 표에 정리하세요. 주거·공과금·식비·필수 교통을 보호하면서 무료 financial counsellor에게 전체 상황을 보여주고, 각 lender의 hardship team에 실제로 감당할 수 있는 금액을 제시합니다. 채권추심 연락은 기록하되 괴롭힘을 받아들일 필요는 없고, 본인 채무가 아니거나 금액이 다르면 서면 증빙을 요구하세요. credit report 오류는 무료로 정정할 수 있지만 정확한 부정적 정보는 돈을 내고 임의로 지울 수 없습니다. consolidation, debt agreement와 bankruptcy는 장기 비용·자산·직업·신용 영향이 있으므로 개인에게 적합하다고 단정하지 말고 독립적인 무료 상담과 법률 조언을 먼저 받으세요.",
  keyPoints: [
    "credit card·loan뿐 아니라 BNPL·utility·phone·fine·세금·가족에게 진 돈까지 한 목록에 정리하세요.",
    "rent·mortgage, utilities, food와 필수 차량처럼 생활과 자산을 지키는 지급기한을 먼저 확인하세요.",
    "lender는 hardship 요청을 검토해야 하지만 사용자가 원하는 조건을 반드시 승인하는 것은 아닙니다.",
    "새 payment plan은 희망 금액이 아니라 실제 예산으로 계속 낼 수 있는 금액만 합의하세요.",
    "debt collector는 연락할 수 있지만 괴롭힘·기만·과도한 연락·제3자에게 채무 공개는 허용되지 않습니다.",
    "court 또는 bankruptcy notice를 받았다면 일반 협상만 계속하지 말고 즉시 무료 법률 조언을 구하세요.",
    "호주에서는 주요 credit reporting body의 credit report를 무료로 받아 서로 다른 기록을 확인할 수 있습니다.",
    "정확한 default나 repayment history는 credit repair 업체가 임의로 지울 수 없으며 오류 정정은 직접 무료로 할 수 있습니다.",
    "debt consolidation은 월 납입액이 낮아 보여도 기간·fee·총이자와 집·차량 담보 위험이 커질 수 있습니다.",
  ],
  sections: [
    {
      id: "inventory-and-verify",
      title: "1. 모든 채무와 법적 기한을 한 표에 모아 사실부터 확인하세요",
      body: [
        "각 creditor·collector별로 잔액, interest rate, fee, minimum payment, due date, arrears, secured asset, guarantor와 현재 단계까지 적으세요. credit card·personal loan·mortgage·car loan뿐 아니라 BNPL, phone·internet·utility bill, rent arrears, fine, tax debt와 가족·친구 채무도 포함하세요. bank statement·contract·bill·collection letter를 모아 중복 청구와 이미 낸 금액을 확인하세요.",
        "모르는 collector가 연락했다면 링크로 바로 지불하지 말고 회사와 creditor를 독립적으로 확인하세요. 채무가 본인 것이 아니거나 금액이 다르면 contract, account statement와 debt 계산 근거를 서면으로 요청하고 dispute를 기록하세요. court claim, statement of claim, bankruptcy notice, repossession 또는 eviction 관련 문서는 응답기한이 짧을 수 있으므로 받은 날짜를 표시하고 즉시 법률 도움을 구하세요.",
      ],
      checklist: [
        "creditor·collector 이름과 공식 연락처",
        "원금·이자·fee·연체액과 minimum payment",
        "due date·default·court 또는 notice 기한",
        "secured debt와 담보인 집·차량·물품",
        "guarantor·joint account·co-borrower 여부",
        "contract·statement·payment receipt·연락 기록",
        "본인 채무·금액이 맞는지와 dispute 여부",
      ],
      sourceIds: [
        "debt-credit-source-control",
        "debt-credit-source-collectors",
      ],
    },
    {
      id: "protect-essentials-and-budget",
      title: "2. 필수 생활비를 보호하고 무료 상담으로 감당 가능한 금액을 계산하세요",
      body: [
        "수입과 실제 지출을 기준으로 rent·mortgage, food, electricity·gas, medicine와 일을 유지하는 필수 transport를 먼저 확보하세요. 모든 creditor에게 무리한 약속을 하기 전에 한 달에 채무상환에 실제로 쓸 수 있는 금액을 계산하고, 갑작스러운 비용과 불규칙 수입도 반영하세요. 고금리 채무를 줄이는 원칙이 중요해도 주거 상실·단전·담보물 repossession처럼 즉각적인 결과가 있는 지급을 무시하지 마세요.",
        "National Debt Helpline을 통한 financial counselling은 무료·confidential이며 전체 채무, hardship 신청, creditor 협상과 선택지 비교를 도울 수 있습니다. 상담 전 자료를 모으되 완벽히 준비될 때까지 기다리지 말고 arrears가 커지기 전에 연락하세요. payday loan, credit-card cash advance나 새 BNPL로 기존 연체를 막는 방법은 비용과 부채를 더 키울 수 있습니다.",
      ],
      checklist: [
        "세후 수입과 변동·불규칙 수입",
        "주거·식비·공과금·의료·필수 교통비",
        "이번 주·이번 달 긴급 지급기한",
        "채무상환에 지속 가능한 실제 금액",
        "무료 financial counsellor 상담 예약",
        "새 고비용 credit 없이 버틸 단기 계획",
      ],
      sourceIds: ["debt-credit-source-control"],
    },
    {
      id: "request-hardship",
      title: "3. 각 lender의 hardship team에 서면으로 현실적인 변경을 요청하세요",
      body: [
        "credit 또는 loan repayment를 감당하기 어렵다면 lender의 hardship team에 조기에 연락하세요. 어려움의 원인, 현재 income과 주요 expense, 다른 debts와 감당 가능한 repayment를 설명하고 payment deferral, reduced repayment, term change 또는 payment plan 중 가능한 option을 요청하세요. lender는 hardship assistance 요청을 검토해야 하지만 특정 option이나 전액 면제를 승인해야 하는 것은 아닙니다.",
        "구두 합의 후에도 시작일·종료일·amount·interest와 fee 처리·missed payment 표시·review date를 서면으로 받으세요. 합의 금액을 낼 수 없게 되면 다시 즉시 연락해 변경을 요청하고, 감당할 수 없는 금액에 동의하지 마세요. 거절되면 이유와 internal complaint 방법을 요청하고 필요하면 AFCA 또는 무료 counsellor의 도움을 확인하세요.",
      ],
      checklist: [
        "hardship 원인과 시작 시점",
        "income·major expenses·다른 debts",
        "실제로 감당 가능한 amount와 payment frequency",
        "요청한 deferral·reduced payment·term change",
        "interest·fee·arrears 처리 방식",
        "written arrangement·reference·review date",
        "거절 이유와 internal complaint 경로",
      ],
      sourceIds: ["debt-credit-source-hardship"],
    },
    {
      id: "handle-collectors-and-legal-action",
      title: "4. 채권추심 기록을 남기고 괴롭힘·분쟁·법원서류에 다르게 대응하세요",
      body: [
        "collector와의 모든 연락 날짜·시간·직원·회사·방식·내용을 기록하세요. 본인 채무이고 금액이 맞다면 budget에 맞는 plan을 제안하되 감당하지 못할 금액에 동의하지 마세요. debt collection 자체는 합법이지만 collector는 privacy를 존중해야 하고 abusive language, harassment, deception, unreasonable contact와 동의 없는 제3자 공개를 해서는 안 됩니다.",
        "채무가 아니거나 이미 납부했거나 금액이 틀리면 이유와 증거를 서면으로 보내고 investigation 중 어떤 collection action이 중단되는지 확인하세요. 폭력 위협은 police에 신고하고, harassment가 계속되면 서면 중단 요청과 AFCA 등 관할 complaint를 검토하세요. court notice나 bankruptcy notice는 collector complaint와 별개의 법적 기한이므로 무시하지 말고 community legal centre 또는 Legal Aid 등 무료 법률 조언을 즉시 이용하세요.",
      ],
      checklist: [
        "모든 collection contact의 날짜·시간·내용",
        "채무 ownership·amount·계약 증빙",
        "affordable payment proposal와 written response",
        "harassment·privacy breach·threat evidence",
        "creditor·collector internal complaint number",
        "court·bankruptcy·repossession notice와 deadline",
        "financial counsellor·legal advice·police 필요 여부",
      ],
      sourceIds: ["debt-credit-source-collectors"],
    },
    {
      id: "check-credit-and-options",
      title: "5. credit report를 정정하고 consolidation·insolvency는 장기 결과까지 비교하세요",
      body: [
        "Experian과 Equifax의 credit report를 각각 받아 이름·주소, account, repayment history, hardship arrangement, default, credit application, judgment와 insolvency 정보를 확인하세요. Moneysmart는 무료 report를 3개월마다 받을 권리가 있다고 안내합니다. 두 기관의 정보가 다를 수 있으므로 둘 다 확인하고, 본인 것이 아닌 debt·중복·금액·날짜 오류는 reporting body와 provider에 무료 정정을 요청하세요.",
        "정확한 negative information은 credit repair 업체에 돈을 내도 임의로 삭제할 수 없습니다. correction 과정은 직접 무료로 할 수 있고 해결되지 않으면 AFCA 가능 여부를 확인하세요. score를 즉시 올리거나 loan approval을 보장한다는 광고를 믿지 마세요.",
        "consolidation·refinancing은 rate뿐 아니라 application·legal·valuation·early-exit fee, loan term과 총이자를 기존 debts와 비교하세요. unsecured debt를 집·차량 담보 debt로 바꾸면 payment failure 때 자산을 잃을 위험이 생깁니다. debt agreement와 bankruptcy는 formal legal options이며 credit, employment, travel, directorship와 public insolvency record에 장기 영향이 있을 수 있으므로 등록 administrator에게 바로 서명하기 전에 무료 financial counsellor와 독립 법률 조언을 받으세요.",
      ],
      sourceIds: [
        "debt-credit-source-credit-report",
        "debt-credit-source-credit-repair",
        "debt-credit-source-consolidation",
        "debt-credit-source-insolvency",
      ],
    },
  ],
  faqs: [
    {
      id: "debt-credit-hardship-score",
      question: "financial hardship를 신청하면 credit score가 바로 나빠지나요?",
      answer:
        "Moneysmart는 hardship arrangement 자체가 credit score에 영향을 주지 않으며 arrangement 정보가 report에 제한적으로 표시되고 12개월 후 삭제된다고 안내합니다. 다만 기존 arrears·default와 arrangement 준수 여부는 별도이므로 lender의 서면 설명과 credit report를 확인하세요.",
      sourceIds: [
        "debt-credit-source-hardship",
        "debt-credit-source-credit-report",
      ],
    },
    {
      id: "debt-credit-lender-must-approve",
      question: "hardship를 요청하면 lender가 제가 원하는 plan을 반드시 승인해야 하나요?",
      answer:
        "아닙니다. lender는 요청을 검토하고 가능한 지원을 평가해야 하지만 특정 조건을 승인해야 한다고 단정할 수 없습니다. 거절되면 이유와 complaint 경로를 요청하고 무료 counsellor와 대안을 검토하세요.",
      sourceIds: ["debt-credit-source-hardship"],
    },
    {
      id: "debt-credit-free-report",
      question: "credit report는 무료로 얼마나 자주 받을 수 있나요?",
      answer:
        "Moneysmart는 무료 credit report를 3개월마다 받을 권리가 있다고 안내합니다. Experian과 Equifax가 서로 다른 정보를 보유할 수 있으므로 두 곳의 current 신청 절차와 신원확인 요건을 확인하세요.",
      sourceIds: ["debt-credit-source-credit-report"],
    },
    {
      id: "debt-credit-delete-default",
      question: "돈을 내면 정확한 default도 credit report에서 삭제할 수 있나요?",
      answer:
        "정확한 부정적 정보는 임의로 지울 수 없습니다. 오류는 reporting body나 provider에 무료로 정정 요청할 수 있지만, credit repair 회사가 모든 negative listing을 삭제하거나 score 향상을 보장한다는 주장은 경계하세요.",
      sourceIds: ["debt-credit-source-credit-repair"],
    },
    {
      id: "debt-credit-not-mine",
      question: "collector가 요구하는 채무가 제 것이 아닌 것 같습니다.",
      answer:
        "즉시 지불하거나 개인정보를 더 주기 전에 creditor·collector를 공식 채널로 확인하고 contract, account statement와 금액 근거를 서면으로 요청하세요. 본인 채무가 아니라는 이유와 증거를 기록해 dispute하고 credit report의 identity theft 가능성도 확인하세요.",
      sourceIds: [
        "debt-credit-source-collectors",
        "debt-credit-source-credit-report",
      ],
    },
    {
      id: "debt-credit-family-contact",
      question: "collector가 가족이나 직장에 제 빚을 말해도 되나요?",
      answer:
        "collector는 privacy를 존중해야 하며 동의 없이 다른 사람에게 debt를 논의해서는 안 됩니다. 연락 내용과 사람을 기록하고 서면 complaint와 AFCA 등 관할 기관을 확인하세요.",
      sourceIds: ["debt-credit-source-collectors"],
    },
    {
      id: "debt-credit-court-notice",
      question: "법원이나 bankruptcy notice를 받았지만 creditor와 협상 중입니다.",
      answer:
        "협상만 믿고 notice를 무시하지 마세요. 별도의 법적 응답기한과 결과가 있을 수 있으므로 문서를 받은 즉시 무료 법률 조언을 구하고, creditor와의 모든 합의를 서면으로 확인하세요.",
      sourceIds: [
        "debt-credit-source-collectors",
        "debt-credit-source-insolvency",
      ],
    },
    {
      id: "debt-credit-consolidation",
      question: "여러 빚을 한 대출로 합치면 항상 유리한가요?",
      answer:
        "아닙니다. 새 rate·fee·term·총비용과 조기상환 비용을 비교해야 하고, 기간이 길어지면 낮은 rate라도 총이자가 늘 수 있습니다. 집이나 차를 담보로 바꾸면 자산 손실 위험도 생깁니다.",
      sourceIds: ["debt-credit-source-consolidation"],
    },
    {
      id: "debt-credit-debt-agreement",
      question: "debt agreement는 consolidation loan과 같은가요?",
      answer:
        "아닙니다. Part IX debt agreement는 creditor와 체결하는 formal insolvency option이며 credit report, National Personal Insolvency Index와 일부 직업에 장기 영향이 있을 수 있습니다. 등록 administrator 비용과 조건을 이해하고 무료 상담을 먼저 받으세요.",
      sourceIds: ["debt-credit-source-insolvency"],
    },
    {
      id: "debt-credit-bankruptcy",
      question: "빚이 많으면 bankruptcy가 가장 빠른 해결책인가요?",
      answer:
        "개인 상황에 따라 결과가 크게 달라지며 자동으로 적합한 선택이 아닙니다. 자산·소득·직업·travel·credit와 public record에 중대한 결과가 있으므로 다른 option을 검토하고 financial counsellor와 독립 법률 조언을 먼저 받으세요.",
      sourceIds: ["debt-credit-source-insolvency"],
    },
  ],
  sourceIds: [
    "debt-credit-source-control",
    "debt-credit-source-hardship",
    "debt-credit-source-collectors",
    "debt-credit-source-credit-report",
    "debt-credit-source-credit-repair",
    "debt-credit-source-consolidation",
    "debt-credit-source-insolvency",
  ],
  additionalChecks: [
    "모든 court·bankruptcy·repossession·eviction notice의 실제 응답기한을 문서 원문과 법률 조언으로 즉시 확인하세요.",
    "hardship arrangement의 interest, fee, term, credit reporting와 missed-payment 처리방식을 lender 서면에서 확인하세요.",
    "collector complaint는 creditor·collector 유형에 따라 AFCA·ACCC·ASIC·state consumer agency 관할이 다를 수 있습니다.",
    "credit report의 current 무료 신청주기, identity check와 dispute 처리기한을 각 reporting body에서 확인하세요.",
    "consolidation offer는 comparison rate, 모든 fee, loan term, total repayment와 secured asset 위험을 계약 전에 재확인하세요.",
    "bankruptcy·debt agreement의 current eligibility, indexed amounts, exclusions와 직업 영향은 AFSA와 독립 법률 조언에서 확인하세요.",
  ],
  disclaimer:
    "이 콘텐츠는 호주의 개인채무, hardship, 채권추심과 credit report에 관한 R3 테스트 미리보기 생활정보이며 법률·금융·insolvency 자문이 아닙니다. lender 승인, debt waiver, credit score 개선, default 삭제, AFCA 결과, debt agreement 또는 bankruptcy의 적합성을 개인에게 확정하지 않습니다. 채무 종류, 담보, 법원기한, 계약·credit law와 개인 재정에 따라 결과가 달라지므로 무료 financial counsellor, lender, credit reporting body와 필요한 경우 독립 법률 조언의 최신 안내를 확인하세요.",
} as const satisfies TopicGuide;

export const DEBT_CREDIT_SOURCES = [
  {
    sourceId: "debt-credit-source-control",
    topicId: "debt-credit",
    title: "Get debt under control",
    publisher: "Moneysmart",
    url: "https://moneysmart.gov.au/managing-debt/get-debt-under-control",
    trust: 5,
    jurisdiction: "australia",
    checkedAt: "2026-07-16",
    verificationMethod:
      "Moneysmart 공식 본문에서 전체 debt 목록, budget·priority payments, creditor 조기 연락과 무료 financial counselling 순서 확인",
    reviewStatus: "live_verified",
    claimScope: "개인채무 inventory, 필수지출 보호, affordable repayment와 초기 지원",
    followUp: "National Debt Helpline의 current hours·contact와 개인 debt 유형별 urgent support 재확인",
  },
  {
    sourceId: "debt-credit-source-hardship",
    topicId: "debt-credit",
    title: "Financial hardship",
    publisher: "Moneysmart",
    url: "https://moneysmart.gov.au/managing-debt/financial-hardship",
    trust: 5,
    jurisdiction: "australia",
    checkedAt: "2026-07-16",
    verificationMethod:
      "2026년 6월 18일 갱신된 Moneysmart 본문에서 lender 고려의무, income·expense·affordable amount, arrangement·거절 이유·credit-report 표시 확인",
    reviewStatus: "live_verified",
    claimScope: "credit·loan hardship request와 agreement·complaint의 기본 절차",
    followUp: "개별 lender hardship policy, evidence, interest·fee와 complaint time limit 재확인",
  },
  {
    sourceId: "debt-credit-source-collectors",
    topicId: "debt-credit",
    title: "Dealing with debt collectors",
    publisher: "Moneysmart",
    url: "https://moneysmart.gov.au/managing-debt/dealing-with-debt-collectors",
    trust: 5,
    jurisdiction: "australia",
    checkedAt: "2026-07-16",
    verificationMethod:
      "2026년 6월 18일 갱신된 Moneysmart 본문에서 collector 권한·contact 제한·privacy·금지행위·record keeping·court notice 대응 확인",
    reviewStatus: "live_verified",
    claimScope: "consumer debt collection 연락, affordable plan, dispute·harassment complaint와 legal escalation",
    followUp: "creditor·collector 유형별 current AFCA·regulator 관할과 법원 notice 기한 재확인",
  },
  {
    sourceId: "debt-credit-source-credit-report",
    topicId: "debt-credit",
    title: "Credit scores and credit reports",
    publisher: "Moneysmart",
    url: "https://moneysmart.gov.au/managing-debt/credit-scores-and-credit-reports",
    trust: 5,
    jurisdiction: "australia",
    checkedAt: "2026-07-16",
    verificationMethod:
      "2026년 6월 18일 갱신된 Moneysmart 본문에서 3개월별 무료 report 권리, Experian·Equifax, report 내용, hardship·default와 무료 오류정정 확인",
    reviewStatus: "live_verified",
    claimScope: "credit report·score 확인, 주요 listing과 오류·identity-theft 점검",
    followUp: "reporting bodies의 current free request·identity verification·correction process 재확인",
  },
  {
    sourceId: "debt-credit-source-credit-repair",
    topicId: "debt-credit",
    title: "Credit repair",
    publisher: "Moneysmart",
    url: "https://moneysmart.gov.au/managing-debt/credit-repair",
    trust: 5,
    jurisdiction: "australia",
    checkedAt: "2026-07-16",
    verificationMethod:
      "2026년 6월 30일 갱신된 Moneysmart 본문에서 agency·provider 오류 무료 정정, 정확한 negative 정보 제한, licensed company와 high-fee 경고 확인",
    reviewStatus: "live_verified",
    claimScope: "credit report 오류의 무료 correction과 credit-repair 업체 한계",
    followUp: "개별 correction dispute, AFCA eligibility와 credit provider response 재확인",
  },
  {
    sourceId: "debt-credit-source-consolidation",
    topicId: "debt-credit",
    title: "Debt consolidation and refinancing",
    publisher: "Moneysmart",
    url: "https://moneysmart.gov.au/managing-debt/debt-consolidation-and-refinancing",
    trust: 5,
    jurisdiction: "australia",
    checkedAt: "2026-07-16",
    verificationMethod:
      "2026년 6월 18일 갱신된 Moneysmart 본문에서 rate·fee·term·총비용, secured asset 위험, licensed provider와 무료 상담 확인",
    reviewStatus: "live_verified",
    claimScope: "개인 debt consolidation·refinancing의 비용·기간·담보 위험 비교",
    followUp: "실제 offer의 comparison rate, fees, total repayment, security와 licence 재확인",
  },
  {
    sourceId: "debt-credit-source-insolvency",
    topicId: "debt-credit",
    title: "Bankruptcy and debt agreements",
    publisher: "Moneysmart",
    url: "https://moneysmart.gov.au/managing-debt/bankruptcy-and-debt-agreements",
    trust: 5,
    jurisdiction: "australia",
    checkedAt: "2026-07-16",
    verificationMethod:
      "2026년 6월 18일 갱신된 Moneysmart 본문에서 formal option의 장기 결과, bankruptcy·Part IX 차이, AFSA administrator와 즉시 legal advice 필요성 확인",
    reviewStatus: "live_verified",
    claimScope: "bankruptcy·debt agreement의 일반 구조와 credit·career·public record 결과",
    followUp: "AFSA current eligibility·indexed amounts·excluded debts·administrator fees와 개인 법률영향 재확인",
  },
] as const satisfies readonly TopicContentSource[];

const mutableGuides = TOPIC_GUIDES as unknown as TopicGuide[];
if (!mutableGuides.some((guide) => guide.topicId === DEBT_CREDIT_GUIDE.topicId)) {
  mutableGuides.push(DEBT_CREDIT_GUIDE);
}

const mutableSources = TOPIC_CONTENT_SOURCES as unknown as TopicContentSource[];
for (const source of DEBT_CREDIT_SOURCES) {
  if (!mutableSources.some((existing) => existing.sourceId === source.sourceId)) {
    mutableSources.push(source);
  }
}
